import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { cardService } from '@/features/card/services/cardService/cardService';
import type { Card, CardFilters, UseCardsReturn } from '@/features/card/types/card.types';
import { buildCardListParams } from '@/features/card/utils/cardFilters.utils';
import { isBasic } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAsyncGuard } from '@chikara/hooks';
import { useSpinner } from '@chikara/ui';
import { t } from '@/i18n';
import { formatCardAliasForDisplay, formatCardLabelForDisplay } from '@/features/card/utils/cardDisplayLabels';


export const useCards = (filters: CardFilters = {}): UseCardsReturn => {
    const { userData, payload: authPayload } = useAuth();
    const { show, hide, isSpinnerLoading } = useSpinner();

    // We use refs for auth data to keep fetchCards stable and avoid identity-based loops
    const authRef = useRef({
        userUuid: authPayload?.user_uuid,
        role: userData?.role,
        isBasic: isBasic(userData?.role)
    });

    // Update refs whenever auth data changes, but don't re-create functions
    useEffect(() => {
        authRef.current = {
            userUuid: authPayload?.user_uuid,
            role: userData?.role,
            isBasic: isBasic(userData?.role)
        };
    }, [authPayload?.user_uuid, userData?.role]);

    const [cards, setCards] = useState<Card[]>([]);
    const [catalogs, setCatalogs] = useState<{ statuses: string[]; tags: string[] }>({ statuses: [], tags: [] });
    const [error, setError] = useState<string | null>(null);

    // Filtros aplicados → query params (objeto estable mientras no cambien).
    const requestFilter = useMemo(
        () => buildCardListParams(filters),
        [filters.search, filters.status, filters.type, (filters.tags ?? []).join(','), filters.noLimit]
    );
    const [paginationData, setPaginationData] = useState({
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 10,
        totalItems: 0,
    });

    const { checkGuard } = useAsyncGuard();

    const fetchCards = useCallback(async (page: number = 1, limit: number = 10, _status?: string) => {
        if (!checkGuard()) return;

        show();
        setError(null);
        try {
            const { userUuid, isBasic: basicUser } = authRef.current;

            const params = {
                page,
                limit,
                balance: true,
                // requestFilter ya es buildCardListParams(filters): no volver a mapearlo
                // (la segunda pasada buscaba noLimit y perdía no_limit).
                ...requestFilter,
                ...(basicUser && { user_uuid: userUuid })
            }

            const response = await cardService.listCards(params);
            const cardList = response.data.map((item: any) => {
                return {
                    ...item,
                    uuid: item.account_uuid,
                    email: item.user_info?.email,
                    cardAlias: formatCardAliasForDisplay(item.alias),
                    maskedPan: item.pan_last_4 ? `•••• ${item.pan_last_4}` : '•••• 0000',
                    cardholderName: item.user_info?.fullname,
                    disposable: item.balance?.disposable || 0,
                    creditLimit: item.balance?.assigned_limit || 0,
                    tag: formatCardLabelForDisplay(item.tag),
                    status: item.status || (item.status && item.status !== 'enabled' ? item.status : 'no_card'),
                    card_status: item.status || 'no_card',
                    last_activity: item.last_activity
                }
            })

            setCards(cardList || []);
            setCatalogs({
                statuses: Array.isArray(response.catalogs?.statuses) ? response.catalogs.statuses : [],
                tags: Array.isArray(response.catalogs?.tags) ? response.catalogs.tags : [],
            });
            setPaginationData(prev => ({
                ...prev,
                currentPage: response.pagination.current_page,
                totalPages: response.pagination.total_pages,
                itemsPerPage: response.pagination.per_page,
                totalItems: response.pagination.total_items,
            }));
        } catch (err) {
            setError(t('card.error.getCards'));
            console.error(err);
        } finally {
            hide();
        }
    }, [show, hide, requestFilter,
    ]);

    const handlePageChange = useCallback((page: number, limit: number) => {
        fetchCards(page, limit);
    }, [fetchCards]);

    const pagination = {
        ...paginationData,
        onPageChange: handlePageChange
    };

    useEffect(() => {
        fetchCards();
    }, [fetchCards]);

    return {
        cards,
        catalogs,
        error,
        pagination,
        isSpinnerLoading,
        refetch: fetchCards,
    };
};
